import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Stack,
  Text,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Show, useError, useSuccess } from '@pelicargo/ui';
import { toDollars } from '@pelicargo/utils';
import {
  CargoType,
  CostUnitType,
  FreightService,
  ServiceType,
  SurchargeCategory,
  SurchargeUnit,
  SurchargeWeightType,
} from '@prisma/client';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { trpc } from '../../config/trpc';
import { SurchargeActionState } from '../../utils/types';
import { SurchargeForm } from '../Forms/SurchargeForm';
import { SimpleForm } from '../SimpleForm';

const getLabelFromState = (state: SurchargeActionState) => {
  const mapper = {
    create: 'Create',
    expire: 'Expire',
    replace: 'Replace',
  };

  return mapper[state];
};

const transformEmptyType = (value: any) => (!value ? null : value);

const validationSchema = z.object({
  airline_label: z.string().nullable(),
  provider_id: z.string(),
  surcharge_category: z.nativeEnum(SurchargeCategory),
  airline_code: z.string().nullish(),
  airline_product: z.string().nullish(),
  service_type: z.nativeEnum(ServiceType).nullish(),
  cargo_type: z.nativeEnum(CargoType).nullish(),
  freight_service: z.nativeEnum(FreightService).nullish(),
  origin_airports: z.array(z.string()).nullish(),
  destination_airports: z.array(z.string()).nullish(),
  min_cost: z.coerce.number().nullish(),
  unit: z.nativeEnum(SurchargeUnit).transform(transformEmptyType).nullable(),
  per_unit_cost: z.coerce.number().nullish(),
  per_unit_cost_type: z.nativeEnum(CostUnitType).nullish(),
  starts_at: z.date(),
  expires_at: z.date().nullish(),
  comments: z.string().nullable(),
  is_optional: z.boolean().optional(),
  weight_type: z.nativeEnum(SurchargeWeightType).nullish(),
  account_id: z.coerce.number().nullish(),
});

type FormValues = z.infer<typeof validationSchema>;

type Props = {
  state: SurchargeActionState | null;
  surchargeId: number | null;
  isOpen: boolean;
  onClose: () => void;
};

export const SurchargeDrawer = ({ state, surchargeId, isOpen, onClose }: Props) => {
  const { handleSuccess } = useSuccess();
  const { handleError } = useError();
  const methods = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      weight_type: SurchargeWeightType.CHARGEABLE_WEIGHT,
      per_unit_cost_type: CostUnitType.CURRENCY,
    },
  });

  const { data } = trpc.getSurcharge.useQuery({ id: surchargeId as number }, { enabled: !!surchargeId });

  useEffect(() => {
    if (!surchargeId) {
      methods.reset({}, { keepValues: false });
    }

    if (surchargeId) {
      methods.reset({
        airline_label: data?.airline_label,
        airline_code: data?.airline_code,
        airline_product: data?.airline_product,
        provider_id: data?.provider_id,
        surcharge_category: data?.surcharge_category as SurchargeCategory,
        service_type: data?.service_type as ServiceType,
        cargo_type: data?.cargo_type as CargoType,
        freight_service: data?.freight_service as FreightService,
        weight_type: data?.weight_type,
        origin_airports: data?.origin_airports?.map((node) => node?.airport_as_origin_id) ?? [],
        destination_airports: data?.destination_airports?.map((node) => node?.airport_as_destination_id) ?? [],
        min_cost: toDollars(data?.min_cost),
        unit: data?.unit as SurchargeUnit,
        per_unit_cost: toDollars(data?.per_unit_cost),
        per_unit_cost_type: data?.per_unit_cost_type as CostUnitType,
        starts_at: new Date(data?.starts_at),
        expires_at: data?.expires_at ? new Date(data?.expires_at) : null,
        comments: data?.comments,
        is_optional: data?.is_optional,
        account_id: data?.account_id,
      });
    }
  }, [data, methods, surchargeId]);

  const { mutateAsync: createSurcharge, isLoading: isCreating } = trpc.createSurcharge.useMutation();
  const { mutateAsync: expireSurcharge, isLoading: isExpiring } = trpc.expireSurcharge.useMutation();
  const { mutateAsync: replaceSurcharge, isLoading: isReplacing } = trpc.replaceSurcharge.useMutation();

  const onValid = useCallback(
    async (values: FormValues) => {
      try {
        if (state === 'create') {
          await createSurcharge({
            ...values,
          });
        }
        if (state === 'expire') {
          await expireSurcharge({
            id: surchargeId,
            expires_at: values.expires_at,
          });
        }
        if (state === 'replace') {
          await replaceSurcharge({
            min_cost: values.min_cost,
            per_unit_cost: values.per_unit_cost,
            per_unit_cost_type: values.per_unit_cost_type,
            starts_at: values.starts_at,
            expires_at: values.expires_at,
            replaces_id: surchargeId,
            account_id: values.account_id,
          });
        }

        methods.reset();
        handleSuccess('Success');
        onClose();
      } catch (error) {
        handleError(error);
      }
    },
    [
      createSurcharge,
      expireSurcharge,
      handleError,
      handleSuccess,
      methods,
      onClose,
      replaceSurcharge,
      state,
      surchargeId,
    ],
  );

  const handleSubmit = methods.handleSubmit(onValid);

  const copy = useMemo(() => {
    return `${getLabelFromState(state)} Surcharge`;
  }, [state]);

  const isSubmitting = useMemo(() => isExpiring || isCreating || isReplacing, [isCreating, isExpiring, isReplacing]);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton onClick={onClose} />
        <DrawerHeader>
          <Heading variant="h4">{copy}</Heading>
        </DrawerHeader>
        <DrawerBody>
          <SimpleForm {...methods} submitOnBlur={false}>
            <SurchargeForm action={state} />
          </SimpleForm>
        </DrawerBody>
        <DrawerFooter>
          <Stack direction="row" justify={surchargeId ? 'space-between' : 'right'} align="center" w="full" spacing="2">
            <Show if={!!surchargeId}>
              <Text>ID: {surchargeId}</Text>
            </Show>
            <Button
              type="submit"
              colorScheme={state === 'expire' ? 'red' : 'brand'}
              isLoading={isSubmitting}
              onClick={handleSubmit}
            >
              {copy}
            </Button>
          </Stack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
